import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { findTopicInSurface } from './AnalyticsSurface';
import { SurfaceRegistry } from './SurfaceRegistry';
import { sliceFromParams, type TopicSlice } from './TopicSliceKey';
import { PinnedTilesStore } from './PinnedTilesStore';
import { definitionFromTile } from './PinnedTileTypes';
import type { TopicDefinition } from './TopicCatalogTypes';
import {
    emptyMetrics,
    type AnalyticsFilter,
    type AnalyticsMetric,
    type InteractionRecord,
} from '@/shared/analytics/AnalyticsTypes';

const EVIDENCE_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'users', 'outputTokens', 'totalTokens', 'avgOutputTokens', 'avgConfidence', 'allocatedCostUsd'];
const EVIDENCE_RECORD_LIMIT = 250;

function buildSliceFilters(definition: TopicDefinition, slice: TopicSlice): AnalyticsFilter[] {
    const filters: AnalyticsFilter[] = [{ field: definition.primaryField, values: [slice.primary] }];
    const secondaryField = definition.secondaryField ?? definition.drillField;
    if (slice.secondary !== undefined && secondaryField !== undefined) {
        filters.push({ field: secondaryField, values: [slice.secondary] });
    }
    return filters;
}

/**
 * L2 view model. Resolves a slice into filters, then loads both the raw
 * supporting turns (the evidence) and the slice's rolled-up metrics.
 */
@injectable()
export class SurfaceEvidenceViewModel extends BaseViewModel {
    definition: TopicDefinition | null = null;
    slice: TopicSlice | null = null;
    records: InteractionRecord[] = [];
    totalMatched = 0;
    summary: Record<AnalyticsMetric, number> = emptyMetrics();
    notFound = false;
    loading = false;
    errorMessage: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(PinnedTilesStore) private pinnedTiles: PinnedTilesStore,
        @inject(SurfaceRegistry) private registry: SurfaceRegistry,
    ) {
        super();
    }

    async load(
        surfaceId: string,
        topicId: string | undefined,
        primaryKey: string | undefined,
        secondaryKey: string | undefined,
        ambientFilters: AnalyticsFilter[] = [],
    ): Promise<void> {
        const surface = this.registry.find(surfaceId);
        const fromCatalog = surface === undefined ? undefined : findTopicInSurface(surface, topicId);
        const definition = fromCatalog ?? this.findPinnedDefinition(surfaceId, topicId);
        const slice = sliceFromParams(primaryKey, secondaryKey);
        if (definition === undefined || slice === null) {
            this.notFound = true;
            this.notifyChange();
            return;
        }
        const filters = [...buildSliceFilters(definition, slice), ...ambientFilters];
        this.notFound = false;
        this.definition = definition;
        this.slice = slice;
        this.records = [];
        this.totalMatched = 0;
        this.summary = emptyMetrics();
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [recordsResult, aggregate] = await Promise.all([
                this.gateway.records({
                    source: 'interactions',
                    filters,
                    orderBy: { column: 'timestamp', direction: 'desc' },
                    limit: EVIDENCE_RECORD_LIMIT,
                }),
                this.gateway.aggregate({ source: 'interactions', groupBy: [definition.primaryField], metrics: EVIDENCE_METRICS, filters }),
            ]);
            this.records = recordsResult.records;
            this.totalMatched = recordsResult.totalMatched;
            this.summary = aggregate.totals;
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    private findPinnedDefinition(surfaceId: string, topicId: string | undefined): TopicDefinition | undefined {
        if (topicId === undefined) return undefined;
        const tile = this.pinnedTiles.tilesFor(surfaceId).find((entry) => entry.id === topicId);
        return tile === undefined ? undefined : definitionFromTile(tile);
    }
}
